import React, { useState, useEffect, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { Switch } from '@headlessui/react'

import classNames from '@/utils/classNamesLocal'
import useQuery from '@/hooks/useQuery'
import { useGlobalState } from '@/state'
import { randomToastSuccess } from '@/shared/Toast'
import TipTapTextarea from '@/shared/TipTapTextarea'
import PrimaryButton from '@/shared/Buttons/Primary'
import Card from '@/shared/Card'

const selectClass = 'mt-1 block w-full rounded-md border-gray-300 bg-white py-2 px-3 shadow-sm focus:border-cccpurple focus:outline-none focus:ring-cccpurple dark:bg-gray-900 dark:text-gray-100 sm:text-sm'

// Rates, what you want, union status, timezone. Availability moved to the
// Profile tab since it is the flag people flip most.
const ProfileWorkingInformation = () => {
  const [user, setUser] = useGlobalState('userContainerUser')
  const [, setToast] = useGlobalState('toast')
  const [timezones, setTimezones] = useState([])
  const tipTapIdx = useRef(1)
  const { putpostRequest, getRequest } = useQuery()
  const { formState, reset, register, unregister, watch, handleSubmit, setValue, getValues } = useForm({
    defaultValues: {
      pricing: user.pricing || '',
      lookingFor: user.lookingFor || '',
      isAvailableNonUnion: !!user.isAvailableNonUnion,
      timezone: user.timezone
    }
  })
  const { isDirty } = formState
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    getRequest('/api/v3/static/timezones', {}, (err, jsonData) => {
      if (err) { return }
      setTimezones(jsonData.timezones || [])
      setValue('timezone', user.timezone, { shouldDirty: false })
    })
  }, [])

  useEffect(() => {
    register('pricing'); register('lookingFor'); register('isAvailableNonUnion')
    return () => { unregister('pricing'); unregister('lookingFor'); unregister('isAvailableNonUnion') }
  }, [register])

  const set = (key, val) => setValue(key, val, { shouldDirty: true, shouldValidate: true })

  const onSubmit = (data) => {
    setLoading(true)
    const profile = { pricing: data.pricing, looking_for: data.lookingFor, timezone: data.timezone, is_available_non_union: data.isAvailableNonUnion }
    putpostRequest(`/api/v3/profiles/${user.profileId}`, 'PATCH', { profile }, (err) => {
      setLoading(false)
      if (err) { return }
      setUser({ ...user, pricing: data.pricing, lookingFor: data.lookingFor, timezone: data.timezone, isAvailableNonUnion: data.isAvailableNonUnion })
      reset({ ...getValues() })
      setToast(randomToastSuccess())
    })
  }

  const nonUnion = watch('isAvailableNonUnion')

  return <div className='md:grid md:grid-cols-4 md:gap-6'>
    <div className='mb-4 md:mb-0'>
      <h3 className='text-lg font-semibold text-gray-900 dark:text-gray-100'>Rates and what you want</h3>
      <p className='mt-1 text-sm text-gray-600 dark:text-gray-300'>Producers read this before they message you. Past work goes on your <a href='/credits'>credits</a>.</p>
    </div>
    <Card footer={<div className='flex justify-end'><PrimaryButton onClick={handleSubmit(onSubmit)} loading={loading} disabled={!isDirty} isDirty={isDirty} text='Save' /></div>}>
      <form className='space-y-6'>
        <div>
          <label className='block text-sm font-medium text-gray-700 dark:text-gray-300'>Rates</label>
          <p className='mb-1 text-xs text-gray-500 dark:text-gray-400'>What you charge, or how you like to talk about it.</p>
          <TipTapTextarea html={getValues().pricing} idx={tipTapIdx.current} updateHtml={(v) => set('pricing', v)}
            placeholder='$50 per finished minute for narration. Character work quoted per project. Happy to talk about indie budgets.' />
        </div>
        <div>
          <label className='block text-sm font-medium text-gray-700 dark:text-gray-300'>What you're looking for</label>
          <p className='mb-1 text-xs text-gray-500 dark:text-gray-400'>The kind of projects you want, and what you want out of them.</p>
          <TipTapTextarea html={getValues().lookingFor} idx={tipTapIdx.current} updateHtml={(v) => set('lookingFor', v)}
            placeholder="Roles that show off a nasally villain voice. When I'm not voice acting I collaborate on writing mysteries." />
        </div>
        <div className='grid gap-6 sm:grid-cols-2'>
          <div>
            <label htmlFor='timezone' className='block text-sm font-medium text-gray-700 dark:text-gray-300'>Timezone</label>
            <select id='timezone' {...register('timezone')} className={selectClass}>
              { timezones.map((tz) => <option key={tz.computer} value={tz.computer}>{tz.human.replace(/_/g, ' ')}</option>) }
            </select>
          </div>
          <Switch.Group as='div' className='flex items-center justify-between gap-x-3 rounded-lg bg-gray-50 p-3 dark:bg-gray-900'>
            <Switch.Label as='span' className='flex flex-col'>
              <span className='text-sm font-medium text-gray-900 dark:text-gray-200'>Open to non-union work</span>
              <span className='text-xs text-gray-500 dark:text-gray-400'>No union contract? Leave this on.</span>
            </Switch.Label>
            <Switch checked={!!nonUnion} onChange={() => set('isAvailableNonUnion', !nonUnion)}
              className={classNames(nonUnion ? 'bg-cccpurple' : 'bg-gray-300 dark:bg-gray-500', 'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-cccpurple focus:ring-offset-2')}>
              <span aria-hidden='true' className={classNames(nonUnion ? 'translate-x-5' : 'translate-x-0', 'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition')} />
            </Switch>
          </Switch.Group>
        </div>
      </form>
    </Card>
  </div>
}

export default ProfileWorkingInformation
